// Lightweight theme-aware chart primitives for the Securities Research suite.
// Hand-rolled SVG/div rendering — no chart library, dark mode via Tailwind classes.

import { SECTORS, type Sector } from '../../types/research'
import type { McHistogramBin } from '../../types/research'

// ─── Shared controls ─────────────────────────────────────────

/** Single-select sector chip row (All + each sector). */
export function SectorChips({ value, onChange }: { value: Sector | null; onChange: (s: Sector | null) => void }) {
  const chip = (active: boolean) =>
    `px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
      active
        ? 'bg-blue-600 text-white'
        : 'bg-slate-200/70 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
    }`
  return (
    <div className="flex flex-wrap gap-1.5">
      <button onClick={() => onChange(null)} className={chip(value === null)}>
        All sectors
      </button>
      {SECTORS.map((s) => (
        <button key={s} onClick={() => onChange(value === s ? null : s)} className={chip(value === s)}>
          {s}
        </button>
      ))}
    </div>
  )
}

export function StatTile({
  label,
  value,
  sub,
  tone = 'neutral',
}: {
  label: string
  value: string
  sub?: string
  tone?: 'neutral' | 'good' | 'bad' | 'accent'
}) {
  const valueCls =
    tone === 'good'
      ? 'text-emerald-600 dark:text-emerald-400'
      : tone === 'bad'
        ? 'text-rose-600 dark:text-rose-400'
        : tone === 'accent'
          ? 'text-blue-600 dark:text-blue-400'
          : 'text-slate-900 dark:text-white'
  return (
    <div className="bg-slate-100/70 dark:bg-slate-900/50 rounded-lg px-3 py-2.5">
      <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate" title={label}>
        {label}
      </p>
      <p className={`text-lg font-semibold ${valueCls}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-500 dark:text-slate-400">{sub}</p>}
    </div>
  )
}

// ─── Signed horizontal bar (value around a center axis) ──────

export function SignedBar({
  value,
  maxAbs,
  positiveCls = 'bg-emerald-500/80',
  negativeCls = 'bg-rose-500/80',
}: {
  value: number
  maxAbs: number
  positiveCls?: string
  negativeCls?: string
}) {
  const half = Math.min(1, Math.abs(value) / (maxAbs || 1)) * 50
  return (
    <div className="relative h-3.5 w-full rounded bg-slate-200/60 dark:bg-slate-700/40 overflow-hidden">
      <div className="absolute inset-y-0 left-1/2 w-px bg-slate-400/60 dark:bg-slate-500/60" />
      <div
        className={`absolute inset-y-0.5 rounded-sm ${value >= 0 ? positiveCls : negativeCls}`}
        style={value >= 0 ? { left: '50%', width: `${half}%` } : { right: '50%', width: `${half}%` }}
      />
    </div>
  )
}

// ─── Histogram with fitted-normal overlay ────────────────────

export function HistogramChart({
  bins,
  overlay,
  markers,
  formatValue,
}: {
  bins: McHistogramBin[]
  overlay: { x: number; y: number }[]
  /** stroke/fill must be literal Tailwind classes (JIT scanning) */
  markers: { value: number; label: string; stroke: string; fill: string }[]
  formatValue: (v: number) => string
}) {
  if (bins.length === 0) return null
  const W = 660
  const H = 235
  const padL = 8
  const padR = 8
  const padT = 26
  const padB = 22
  const lo = bins[0].start
  const hi = bins[bins.length - 1].end
  const maxCount = Math.max(...bins.map((b) => b.count), ...overlay.map((p) => p.y), 1)
  const x = (v: number) => padL + ((v - lo) / (hi - lo)) * (W - padL - padR)
  const y = (c: number) => H - padB - (c / maxCount) * (H - padT - padB)

  const overlayPath = overlay.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.x).toFixed(1)},${y(p.y).toFixed(1)}`).join(' ')
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => lo + (hi - lo) * f)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Outcome distribution histogram">
      {/* bars */}
      {bins.map((b, i) => (
        <rect
          key={i}
          x={x(b.start) + 0.5}
          y={y(b.count)}
          width={Math.max(1, x(b.end) - x(b.start) - 1)}
          height={H - padB - y(b.count)}
          className={b.end <= 0 ? 'fill-rose-400/50 dark:fill-rose-500/40' : 'fill-blue-500/55 dark:fill-blue-400/50'}
        />
      ))}
      {/* fitted normal */}
      <path d={overlayPath} fill="none" strokeWidth={1.8} className="stroke-slate-600 dark:stroke-slate-300" strokeDasharray="1 0" />
      {/* markers */}
      {markers.map((m, i) => (
        <g key={i}>
          <line x1={x(m.value)} x2={x(m.value)} y1={padT - 4} y2={H - padB} strokeDasharray="4 3" strokeWidth={1.2} className={m.stroke} />
          <text x={x(m.value)} y={padT - 9} textAnchor="middle" className={`${m.fill} text-[10px]`}>
            {m.label}
          </text>
        </g>
      ))}
      {/* baseline + x ticks */}
      <line x1={padL} x2={W - padR} y1={H - padB} y2={H - padB} strokeWidth={1} className="stroke-slate-300 dark:stroke-slate-600" />
      {ticks.map((t, i) => (
        <text
          key={i}
          x={x(t)}
          y={H - 7}
          textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'}
          className="fill-slate-500 dark:fill-slate-400 text-[10px]"
        >
          {formatValue(t)}
        </text>
      ))}
    </svg>
  )
}

// ─── Sample paths ────────────────────────────────────────────

export function PathsChart({ paths, startValue, formatValue }: { paths: number[][]; startValue: number; formatValue: (v: number) => string }) {
  if (paths.length === 0) return null
  const W = 660
  const H = 190
  const padL = 8
  const padR = 52
  const padT = 8
  const padB = 8
  let lo = Infinity
  let hi = -Infinity
  for (const p of paths) for (const v of p) {
    if (v < lo) lo = v
    if (v > hi) hi = v
  }
  if (lo === hi) {
    lo -= 1
    hi += 1
  }
  const n = paths[0].length
  const x = (i: number) => padL + (i / (n - 1)) * (W - padL - padR)
  const y = (v: number) => padT + (1 - (v - lo) / (hi - lo)) * (H - padT - padB)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Simulated portfolio paths">
      <line x1={padL} x2={W - padR} y1={y(startValue)} y2={y(startValue)} strokeDasharray="4 3" strokeWidth={1} className="stroke-slate-400 dark:stroke-slate-500" />
      {paths.map((p, i) => (
        <polyline
          key={i}
          fill="none"
          strokeWidth={1}
          className="stroke-blue-500/30 dark:stroke-blue-400/30"
          points={p.map((v, j) => `${x(j).toFixed(1)},${y(v).toFixed(1)}`).join(' ')}
        />
      ))}
      {[hi, startValue, lo].map((v, i) => (
        <text key={i} x={W - padR + 4} y={y(v) + 3} className="fill-slate-500 dark:fill-slate-400 text-[10px]">
          {formatValue(v)}
        </text>
      ))}
    </svg>
  )
}

// ─── Tornado (two-sided sensitivity) ─────────────────────────

export function TornadoChart({
  rows,
  base,
  formatValue,
}: {
  rows: { label: string; low: number; high: number }[]
  base: number
  formatValue: (v: number) => string
}) {
  const extent = Math.max(...rows.flatMap((r) => [Math.abs(r.low - base), Math.abs(r.high - base)]), 1e-9)
  const seg = (v: number) => {
    const offset = v - base
    const width = (Math.abs(offset) / extent) * 50
    return offset >= 0 ? { left: '50%', width: `${width}%` } : { right: '50%', width: `${width}%` }
  }
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.label} className="grid grid-cols-[minmax(0,11rem)_1fr] gap-3 items-center">
          <span className="text-xs text-slate-600 dark:text-slate-300 truncate" title={r.label}>
            {r.label}
          </span>
          <div className="relative h-7 rounded bg-slate-200/60 dark:bg-slate-700/40" title={`−20%: ${formatValue(r.low)} · +20%: ${formatValue(r.high)}`}>
            <div className="absolute inset-y-0 left-1/2 w-px bg-slate-400/70 dark:bg-slate-500/70" />
            <div className="absolute top-1 h-2 rounded-sm bg-amber-500/80" style={seg(r.low)} />
            <div className="absolute bottom-1 h-2 rounded-sm bg-blue-500/80" style={seg(r.high)} />
          </div>
        </div>
      ))}
      <div className="grid grid-cols-[minmax(0,11rem)_1fr] gap-3">
        <span />
        <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
          <span>
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-500/80 mr-1 align-middle" />
            parameter −20%
          </span>
          <span>base mean {formatValue(base)}</span>
          <span>
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-blue-500/80 mr-1 align-middle" />
            parameter +20%
          </span>
        </div>
      </div>
    </div>
  )
}
